import { defineQuiz } from "./_schema";
import type { Answers, PersonaKey, Question } from "./_schema";
import { deriveSleepProfile } from "./quiz-sleep-profile";
import { MM_0102 } from "./_experiments";

/**
 * SLEEP QUIZ -- cold-traffic destination for the sleep angle.
 *
 * Promise: "you are in bed the right number of hours and still wake up wrecked."
 * Duration is explicitly NOT the problem -- that is the hook, and it constrains
 * every downstream decision (see quizzes/quiz-sleep-profile.ts for why the
 * verdict needed a new root cause rather than `sleep-deprivation`).
 *
 * Mechanism: cortisol is built for short bursts and should bottom out at night.
 * Modern load keeps it high, so it fires in the early-morning hours instead of at
 * dawn -- the 2-3 AM wake-up -- clipping the deep-sleep window where most
 * testosterone is made. Low T then degrades the next night. The loop feeds itself.
 *
 * 12 single-choice screens + 3 interstitials (2 bridges + analyzing) = 15.
 * Design spec: docs/specs/2026-07-30-quiz-sleep-quiz-design.md.
 * Approved walkthrough: docs/quiz-sleep-quiz-flow.html.
 *
 * No performance ads were available at design time, so the operator directed that
 * the supplied question order be kept exactly. The two additions (`sleep-goal`,
 * `sleep-cost`) therefore BRACKET the supplied ten rather than interleave: the
 * flow had 0 POSITIVE beats and no tolerance check, which inverts the emotional
 * journey. If ads later show a fixed 8-signs panel order that disagrees with this
 * sequence, the ad wins.
 *
 * Like quiz-v6 and cortisol, this quiz has no GLP-1 screener, so `glp1-user` is
 * not reachable from the classifier. It also has no body-composition or
 * afternoon-energy question, so `performance` is not reachable either.
 */

const QUESTION_ORDER = [
  "age",
  "sleep-goal",
  "unrefreshed",
  "wake-pattern",
  "wired-tired",
  "morning-state",
  "info-absolution",
  "dream-recall",
  "sleep-aids",
  "snoring",
  "libido",
  "when-started",
  "sleep-cost",
  "info-mechanism",
  "info-analyzing",
] as const;

const questions: Question[] = [
  {
    // S1 carries the QuizIntro block instead of the progress bar (rule 19), which
    // makes it the fold-critical screen. It is deliberately the SHORT
    // one-line-prompt `age` question, exactly as quiz-v6 / cortisol / quiz-energy
    // do it: measured at 375x667, moving the 3-line `sleep-goal` prompt here
    // instead pushed the second answer pill from 630px (quiz-v6's reference) to
    // 784px. Keep a one-line prompt on whichever screen renders first.
    id: "age",
    type: "single",
    prompt: "How old are you?",
    options: [
      { value: "50-plus", label: "50+ years old" },
      { value: "40-49", label: "40–49 years old" },
      { value: "30-39", label: "30–39 years old" },
      { value: "20-29", label: "20–29 years old" },
    ],
  },
  {
    // The flow's only POSITIVE beat, and it still precedes all eight symptom
    // screens (age is a neutral demographic anchor, not a negative). Commits the
    // user to a want before any pain lands, and feeds `results-want` (otherwise
    // unfed by this quiz). Sits at S2 rather than S1 for the fold reason above.
    id: "sleep-goal",
    type: "single",
    prompt: "If you woke up genuinely rested tomorrow, what would you want back first?",
    options: [
      { value: "energy", label: "Energy that lasts the whole day" },
      { value: "drive", label: "My drive" },
      { value: "focus", label: "Sharp focus again" },
      { value: "all", label: "All of it" },
    ],
  },
  {
    id: "unrefreshed",
    type: "single",
    prompt: "Do you sleep a full night and still wake up feeling exhausted?",
    options: [
      { value: "almost-every-morning", label: "Almost every morning" },
      { value: "most-mornings", label: "Most mornings" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    id: "wake-pattern",
    type: "single",
    prompt: "Do you wake up between 2 and 3 AM for no reason?",
    options: [
      { value: "most-nights", label: "Most nights" },
      { value: "few-times-week", label: "A few times a week" },
      { value: "rarely", label: "Rarely" },
      { value: "never", label: "Never" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      // Deliberately names NO hormone. The intro promises "your hormones could be
      // the reason...", which is an open loop, and info-mechanism is where it
      // closes -- with the operator's cortisol-rhythm chart drawing this exact
      // sentence. The cortisol quiz can name cortisol in its equivalent screen
      // because its ad and intro already do; here it would spend the reveal at
      // screen 4 of 15. Do not re-add "cortisol" to this copy.
      body: "Your body runs sleep on a 24-hour hormonal clock. It’s supposed to bottom out at night and rise at dawn. When it gets stuck high, it fires in the early-morning hours instead — that’s the 2–3 AM wake-up.",
    },
  },
  {
    id: "wired-tired",
    type: "single",
    prompt: "At the end of the day, are you wiped out but too wired to actually fall asleep?",
    options: [
      { value: "almost-every-night", label: "Almost every night" },
      { value: "few-nights-week", label: "A few nights a week" },
      { value: "occasionally", label: "Occasionally" },
      { value: "rarely", label: "Rarely" },
    ],
  },
  {
    // Intensity companion to `unrefreshed` (which measures frequency). Both are
    // kept deliberately -- frequency plus intensity of the same symptom is
    // standard instrument design and does not read as repetition.
    id: "morning-state",
    type: "single",
    prompt: "How do you feel in the first hour after waking?",
    options: [
      { value: "wrecked", label: "Wrecked, I can’t function without caffeine" },
      { value: "groggy", label: "Groggy and slow to get going" },
      { value: "foggy-okay", label: "A little foggy but okay" },
      { value: "sharp", label: "Sharp and ready" },
    ],
  },
  {
    id: "info-absolution",
    type: "info",
    heading: "You’re not imagining it.",
    body: [
      "You’re in bed the right number of hours. You’ve tried the new pillow, the sleep app, and the blackout curtains. And you still wake up drained.",
      "Modern life keeps your body under a constant, low-grade pressure it was never built for, and that pressure quietly rewires the hormones that run your sleep.",
      "Your body makes most of its testosterone while you sleep. So when your sleep breaks down, so does your T, and low testosterone only makes the next night worse. The cycle feeds itself.",
    ],
    // Renders below the Continue CTA (rule 20). Mechanism-silent by design: this
    // is the absolution bridge, and it names testosterone (the UMP hinge) but NOT
    // cortisol -- info-mechanism owns that reveal. Operator-supplied UGC loop of a
    // man tossing in bed. Re-encoded from a 4.8 MB / 718px / 10fps GIF to animated
    // WebP at 672px / 8fps / q45 (56 frames, 464 KB); GIF and animated AVIF both
    // fail on this surface. Note ffmpeg cannot DECODE animated WebP, so a failed
    // probe is not evidence of a broken file -- check the RIFF ANMF chunk count.
    image: {
      src: "/static/images/quiz-sleep-absolution.webp",
      alt: "A man lying in bed at night, restless, throwing an arm over his face",
    },
  },
  {
    id: "dream-recall",
    type: "single",
    prompt: "Do you remember your dreams?",
    options: [
      { value: "cant-remember", label: "Can’t remember the last time I dreamed" },
      { value: "much-less", label: "Much less than I used to" },
      { value: "about-same", label: "About the same" },
      { value: "vivid", label: "I dream vividly" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      // The highest "how did they know that" screen in the flow. Names no hormone,
      // so it cannot leak the info-mechanism reveal.
      body: "Dreams happen in your deepest sleep stages. When you stop remembering them, it usually means you’re not reaching them — and those are the same hours your body does its rebuilding.",
    },
  },
  {
    // Lands AFTER info-absolution by design. The bridge covers the cheap
    // environmental fixes (pillow, app, blackout curtains); this screen catches the
    // users who went further into supplements. Different admissions, so the bridge
    // is not front-running it.
    id: "sleep-aids",
    type: "single",
    prompt: "Do you rely on melatonin, sleep gummies, or other aids and still not sleep well?",
    options: [
      { value: "barely-help", label: "Yes, and they barely help" },
      { value: "mixed", label: "Sometimes, with mixed results" },
      { value: "gave-up", label: "Tried them, gave up" },
      { value: "dont-use", label: "Don’t use them" },
    ],
  },
  {
    id: "snoring",
    type: "single",
    prompt: "Has your snoring or restless, tossing-and-turning sleep gotten worse?",
    options: [
      { value: "noticeably-worse", label: "Noticeably worse" },
      { value: "bit-worse", label: "A bit worse" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
  },
  {
    id: "libido",
    type: "single",
    prompt: "Has your drive dropped compared to a few years ago?",
    options: [
      { value: "significantly", label: "Significantly" },
      { value: "somewhat", label: "Somewhat" },
      { value: "not-sure", label: "Not sure" },
      { value: "no", label: "No" },
    ],
    whyWeAsk: {
      heading: "Why we ask",
      body: "Testosterone and cortisol compete for the same raw materials inside your body. When cortisol runs high, your drive is usually the first thing to fade, and the deep sleep that rebuilds testosterone goes with it.",
    },
  },
  {
    id: "when-started",
    type: "single",
    prompt: "When did you start noticing these changes?",
    options: [
      { value: "over-year", label: "Over a year ago" },
      { value: "past-year", label: "In the past year" },
      { value: "few-months", label: "In the past few months" },
      { value: "not-sure", label: "Not sure" },
    ],
  },
  {
    // Tolerance check (7-phase architecture, phase 4). The only screen that asks
    // what this COSTS rather than how often it happens -- every other negative is a
    // frequency question. Placed immediately before the villain reveal so peak
    // pressure flows into it. Also sets the QoL floor (see quiz-sleep-profile.ts):
    // without it, `high` QoL is unreachable on every path. Note "not much YET" --
    // the mildest option is still a trajectory admission, not an absolution.
    id: "sleep-cost",
    type: "single",
    prompt: "Be honest — how much is the exhaustion taking from you right now?",
    options: [
      { value: "running-my-life", label: "It’s running my life at this point" },
      { value: "more-than-id-admit", label: "More than I’d like to admit" },
      { value: "some-days", label: "Some days it wins" },
      { value: "not-much", label: "Not much yet" },
    ],
  },
  {
    id: "info-mechanism",
    type: "info",
    // Tightened from the operator draft ("These signs all point to one thing: your
    // hormones could be running your nights, and your testosterone could be paying
    // the price.") -- 25 words rendered ~7 lines of uppercase Martian Mono at
    // 375px and pushed the Continue CTA to 700px, vs 465 on cortisol's bridge 2
    // and 600 on quiz-energy's. Both halves of the thesis are kept; the hedge is
    // dropped to match the assertive register of the live cortisol bridge ("cortisol
    // is suppressing your testosterone"), and the body restates the mechanism two
    // sentences later regardless. Restoring the longer heading costs ~50px of fold.
    heading:
      "These signs point to one thing: your hormones are running your nights, and your testosterone is paying the price.",
    body: [
      "Cortisol is your body’s alarm system, built for short bursts. A threat appears, it spikes, the threat passes, it drops, and at night it’s supposed to bottom out so you can sleep.",
      "But modern life never lets it drop. Cortisol that stays high keeps you wired at bedtime, jolts you awake at 3 AM, and slows down the testosterone your deepest, most restorative sleep depends on.",
      // Closing line changed from "Let's see where yours stands" so info-analyzing
      // catches the thread instead of dropping it at peak suspense.
      "Lower the cortisol, free the testosterone, and sleep can finally do its job. Let’s see where your sleep and your testosterone stand.",
    ],
    // First naming of cortisol in the flow, and it lands on the sentence this
    // chart draws. Operator-supplied, on-brand, ships unmodified.
    // DELIBERATE: this is a mechanism diagram, NOT a product shot -- so this flow
    // has zero product appearance before the offer page. Cold-traffic soft sell.
    // Do not "fix" it by swapping in a bottle.
    image: {
      src: "/static/images/quiz-sleep-mechanism.avif",
      alt: "Overnight cortisol rhythm chart: a healthy curve stays low and surges at dawn, while a chronic-stress curve fires hours too early and wakes you at 3 AM",
    },
  },
  {
    id: "info-analyzing",
    type: "info",
    // Pays off info-mechanism's closing line ("Let's see where your sleep and your
    // testosterone stand").
    heading: "Analyzing your sleep profile…",
    body: [
      "Counting the signs you just reported, and analyzing what’s standing between you and a full night’s sleep.",
    ],
    animation: "analyzing",
    autoAdvanceMs: 5000,
  },
];

export const quizSleepQuiz = defineQuiz({
  id: "quiz-sleep",
  slug: "quiz-sleep",
  experiment: MM_0102,
  meta: {
    title: "What’s Really Wrecking Your Sleep?",
    introHeadline: "Can’t sleep? Wake up exhausted? Your hormones could be the reason…",
    introAccent: "hormones",
    introBody:
      "There are 8 signs your hormones are sabotaging your sleep. See how many you have in this 2 minute quiz.",
    testimonial: {
      // Angle-matched (dead tired / can't fall asleep / 3 AM), reveal-safe (names
      // no product or benefit category), unquantified, and says "the quiz" rather
      // than a bare "it". Display name varied off the live cortisol quiz's "MARK"
      // to avoid a collision for anyone who sees both.
      // TODO(operator): back this with a real review. The "Verified Review" suffix
      // is deliberately absent until then -- but note QuizIntro renders
      // <VerifiedTick /> unconditionally, so a tick still shows.
      //
      // Trimmed one rendered line off the operator draft ("took two minutes" was
      // redundant with introBody's "2 minute quiz"). At 375x667 that moved the
      // first answer pill fully above the iPhone SE Safari fold. Restoring the
      // longer quote costs ~26px of fold budget -- re-measure if you do.
      quote:
        "The quiz finally explained why I’m dead tired but still can’t fall asleep, and why I’m up at 3 AM every night. Wish I’d taken it sooner.",
      author: "DAVE",
      // Set explicitly to avoid the shared default, which is a 3840x2560 / 10.1 MB
      // PNG rendered into a 48px circle -- ~900x the bytes needed, above the fold
      // on screen 1, and a landscape gym shot that crops badly round. (That default
      // is still the fallback on 8 other live quizzes; fixing it there is a
      // separate change.) This file is a byte copy of
      // quiz-before-trt-testimonial-avatar.webp (256x256, 13 KB), kept as a
      // per-quiz asset so each quiz's images stay self-contained -- same
      // convention as the shared bridge diagrams. NOTE the same face is "Ryan T."
      // on /pages/quiz-before-trt; operator accepted that on 2026-08-03.
      avatar: "/static/images/quiz-sleep-testimonial-avatar.webp",
    },
    // MM-0102 -- see quizzes/_experiments.ts.
    introVariantTag: "control",
  },
  questions,

  branching(_answers: Answers, currentQuestionId: string): string | null {
    const idx = QUESTION_ORDER.indexOf(currentQuestionId as (typeof QUESTION_ORDER)[number]);
    if (idx < 0) return null;
    const next = QUESTION_ORDER[idx + 1];
    return next ?? null;
  },

  classifier(answers: Answers): PersonaKey {
    const age = answers["age"];
    const onset = answers["when-started"];
    const libido = answers["libido"];
    const unrefreshed = answers["unrefreshed"];
    const morning = answers["morning-state"];

    // Note: sleep-goal, wake-pattern, wired-tired, dream-recall, sleep-aids,
    // snoring, and sleep-cost are not read here -- they are persuasion / severity
    // screens that feed the deriver (quizzes/quiz-sleep-profile.ts), not routing
    // signals. The classifier routes on age, onset, drive, and the two
    // morning-exhaustion screens.
    //
    // `performance` is unreachable: it needs a lean body-composition signal and
    // this flow has no body question (deliberate on-angle guard). `glp1-user` is
    // unreachable for the same reason as quiz-v6/cortisol -- no GLP-1 screener.

    // TRT-alternative: long-running collapse across drive and morning function.
    if (
      onset === "over-year" &&
      libido === "significantly" &&
      (morning === "wrecked" || unrefreshed === "almost-every-morning")
    ) {
      return "trt-alternative";
    }

    // Skeptical buyer: can't place when it started.
    if (onset === "not-sure") {
      return "skeptical-buyer";
    }

    // Aging men: 40+ waking unrefreshed most mornings or more.
    if (
      (age === "50-plus" || age === "40-49") &&
      (unrefreshed === "almost-every-morning" || unrefreshed === "most-mornings")
    ) {
      return "aging-men";
    }

    return "everyday-man";
  },

  deriveProfile: deriveSleepProfile,
});
